import React, { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";
import { formatDateKey, formatDisplayDate } from "./utils";
import { useDateNormalization } from "@shared/hooks/useDateNormalization";
import { normalizeDate } from "@shared/utils/dateUtils";
import {
  AddHolidayPayload,
  Holiday,
  HolidayApiResponse,
  HolidayMap,
} from "../../types";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import {
  useAddHolidayMutation,
  useUpdateHolidayMutation,
  useLeaveTypes,
  useDeleteHolidayMutation,
} from "../../hooks";
import { format } from "date-fns";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

interface AddHolidayFormProps {
  selectedDate: Date | null;
  holidays: HolidayApiResponse;
  holidayMap: HolidayMap;
  // onRemoveHoliday: (date: Date) => void;
  error: string;
  selectedHoliday: Holiday | null;
}

const AddHolidayForm: React.FC<AddHolidayFormProps> = ({
  selectedDate,
  holidays,
  holidayMap,
  // onRemoveHoliday,
  error,
  selectedHoliday,
}) => {
  const [loading, setLoading] = useState(false);
  const { data: leaveTypes } = useLeaveTypes();
  const addHolidayMutation = useAddHolidayMutation();
  const updateHolidayMutation = useUpdateHolidayMutation();
  const deleteHolidayMutation = useDeleteHolidayMutation();
  const { normalizeWithUserFormat } = useDateNormalization();

  // Check if this is edit mode
  const isEditMode = !!selectedHoliday;

  const selectedDateKey = selectedDate ? formatDateKey(selectedDate) : null;

  // Use holidayMap instead of holidays for getting existing holiday
  const existingHoliday =
    selectedDateKey && holidayMap ? holidayMap[selectedDateKey] : null;


  // Look up the holiday name from the API response for the selected date
  const existingHolidayName = selectedDateKey
    ? (holidays?.result?.find((h) => {
        const normalized = normalizeWithUserFormat(h.date);
        return normalized === selectedDateKey;
      })?.name ?? null)
    : null;

    const existingHolidayId = selectedDateKey
    ? (holidays?.result?.find((h) => {
        const normalized = normalizeWithUserFormat(h.date);
        return normalized === selectedDateKey;
      })?.id ?? null)
    : null;

  const holidayFields: FormField[] = React.useMemo(() => {
    const fields: FormField[] = [
      {
        name: "holidayName",
        type: "text",
        label: "Holiday Name",
        required: true,
        placeholder: "Enter holiday name",
        defaultValue: "", // We'll set this via form.setValue
        minLength: 2,
        disabled: !selectedDate || (!!existingHoliday && !isEditMode),
        customValidation: (value: string) => {
          if (!value) return true;
          if (value.length < 2)
            return "Holiday name must be at least 2 characters";
          return true;
        },
        fullWidth: true,
        classes: {
          helperText: "text-xs text-gray-500 mt-1",
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-1",
          input:
            "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          error: "text-red-600 text-sm mt-1",
        },
      },
      {
        name: "holidayType",
        type: "select",
        label: "Holiday Type",
        required: true,
        defaultValue: "", // We'll set this via form.setValue
        disabled: !selectedDate || (!!existingHoliday && !isEditMode),
        options: leaveTypes
          ? leaveTypes.map((dept) => ({
            value: dept.key,
            label: dept.value,
          }))
          : [],
        fullWidth: true,
        classes: {
          helperText: "text-xs text-gray-500 mt-1",
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-1",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white cursor-pointer",
          error: "text-red-600 text-sm mt-1",
        },
      },
    ];
    return fields;
  }, [selectedDate, existingHoliday, isEditMode, leaveTypes]);
  const onRemoveHoliday = (id: any) => {

    deleteHolidayMutation.mutate(id);
    form.reset();
  };
  const form = FormService.createForm({
    fields: holidayFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Reset form when selected date changes (but not when selectedHoliday changes)
  useEffect(() => {
    if (!selectedHoliday) {
      form.reset();
    }
  }, [selectedDate, form, selectedHoliday]);

  // Populate form when selectedHoliday changes
  useEffect(() => {
    if (selectedHoliday) {
      // Set the form values
      form.setValue("holidayName", selectedHoliday.name || "");
      form.setValue("holidayType", selectedHoliday.leaveType?.toString() || "");

      // Clear any existing errors
      form.clearErrors();
    } else {
      // Clear form when no holiday is selected
      form.reset();
    }
  }, [selectedHoliday, form]);

  const remote_utility_id = useRemoteUtilityId();

  const handleSubmit = async (data: {
    holidayName: string;
    holidayType: string;
  }) => {
    try {
      setLoading(true);

      if (data.holidayName.trim() && selectedDate) {
        const payload = {
          name: data.holidayName.trim(),
          date: format(selectedDate, "yyyy-MM-dd"),
          leave_type: Number(data.holidayType),
          remote_utility_id: remote_utility_id,
        };

        if (isEditMode && selectedHoliday) {
          await updateHolidayMutation.mutateAsync(
            {
              payload,
              id: selectedHoliday.id,
            },
            {
              onSuccess: () => {
                logEvent("ONB Holiday Added", {
                  ...payload,
                });
              },
            },
          );
        } else {
          // Add mode - create new holiday
          await addHolidayMutation.mutateAsync(payload);
        }

        form.reset();
      }
    } catch (err) {
      console.error(
        `Error ${isEditMode ? "updating" : "adding"} holiday:`,
        err,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.reset();
  };

  const handleFieldChange = (fieldName: string) => {
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }
  };

  const getHolidayTypeColor = (type: string) => {
    switch (type) {
      case "federal":
        return "bg-red-100 text-red-700 border-red-200";
      case "company":
        return "bg-green-100 text-green-700 border-green-200";
      case "observed":
        return "bg-orange-100 text-orange-700 border-orange-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const renderCustomHeader = () => (
    <div className="mb-6">
      <h3 className="card-heading-text">
        {existingHoliday
          ? "Holiday Details"
          : selectedHoliday
            ? "Edit Holiday"
            : "Add Holiday"}{" "}
        for {selectedDate ? formatDisplayDate(selectedDate) : "Select a Date"}
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {existingHoliday && (
        <div className="mb-6">
          <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="card-heading-text">
                    {existingHolidayName || existingHoliday.type}
                  </h4>
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full border ${getHolidayTypeColor(
                      existingHoliday.type,
                    )}`}
                  >
                    {existingHoliday.type.charAt(0).toUpperCase() +
                      existingHoliday.type.slice(1)}{" "}
                    Holiday
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {formatDisplayDate(selectedDate!)}
                </p>
              </div>
              <button
                onClick={() =>
                  selectedDate && onRemoveHoliday(existingHolidayId)
                }
                className="ml-3 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove holiday"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {!existingHoliday && selectedDate && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {selectedHoliday ? "Edit Holiday" : "Add New Holiday"} for{" "}
            {formatDisplayDate(selectedDate)}:
          </label>
        </div>
      )}

      {!selectedDate && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 text-sm">
            📅 Please select a date from the calendar or holiday list to add or
            view holiday details.
          </p>
        </div>
      )}
    </div>
  );

  const renderCustomFooter = () => (
    <div className="mt-4">
      <Button
        type="submit"
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        disabled={loading || !selectedDate}
      >
        <Plus className="w-4 h-4" />
        {loading
          ? selectedHoliday
            ? "Updating Holiday..."
            : "Adding Holiday..."
          : selectedHoliday
            ? "Update Holiday"
            : "Add Holiday"}
      </Button>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 h-full">
      {renderCustomHeader()}

      <DynamicForm
        fields={holidayFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        loading={loading}
        config={{
          showProgress: false,
          layout: "vertical",
          spacing: "medium",
          submitButtonText: selectedHoliday ? "Update Holiday" : "Add Holiday",
          showResetButton: false,
          resetOnSubmit: false,
          classes: {
            form: "space-y-4",
            fieldContainer: "",
            buttonContainer: "hidden",
            fieldGroup: "",
          },
        }}
        renderCustomFooter={renderCustomFooter}
        style={{ background: "transparent", border: "none", boxShadow: "none" }}
      />
    </div>
  );
};

export default AddHolidayForm;
